import { create } from 'zustand';
import { cx } from '@/lib/cx';
import { Icon } from './Icon';
import s from './Toast.module.css';

type ToastTone = 'info' | 'pos' | 'neg';
interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastState {
  items: ToastItem[];
  push: (tone: ToastTone, message: string) => void;
  dismiss: (id: number) => void;
}

let next = 1;

const useToasts = create<ToastState>((set) => ({
  items: [],
  push: (tone, message) => {
    const id = next++;
    set((st) => ({ items: [...st.items.slice(-3), { id, tone, message }] }));
    setTimeout(() => set((st) => ({ items: st.items.filter((t) => t.id !== id) })), 5000);
  },
  dismiss: (id) => set((st) => ({ items: st.items.filter((t) => t.id !== id) })),
}));

/** Feedback for completed actions ("Strategy saved"). Never used for results or errors that need a decision. */
export const toast = {
  info: (m: string) => useToasts.getState().push('info', m),
  success: (m: string) => useToasts.getState().push('pos', m),
  error: (m: string) => useToasts.getState().push('neg', m),
};

const icon: Record<ToastTone, string> = { info: 'info', pos: 'check_circle', neg: 'error' };

export function Toaster() {
  const items = useToasts((st) => st.items);
  const dismiss = useToasts((st) => st.dismiss);
  return (
    <div className={s.region} role="status" aria-live="polite">
      {items.map((t) => (
        <div key={t.id} className={cx(s.toast, s[t.tone])}>
          <Icon name={icon[t.tone]} size={18} className={s.icon} />
          <span>{t.message}</span>
          <button type="button" className={s.close} onClick={() => dismiss(t.id)} aria-label="Dismiss">
            <Icon name="close" size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}
